import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Webcam from "react-webcam";

import CloseSharpIcon from "@mui/icons-material/CloseSharp";
import { RecognitionStatus } from "types/imageRecognition";
import { useStyles } from "./CameraScan.styles";
import { globalColors } from "assets/colors-global";
import { useTranslation } from "react-i18next";
import { rotateBase64Image } from "utils/dom.util";
import {
  CAPTURE_JPEG_QUALITY,
  getCameraVideoConstraints,
} from "utils/cameraCapture.constants";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";

const statusColor: Record<RecognitionStatus, string> = {
  ERROR: globalColors.error.main,
  PROGRESS: globalColors.warning.main,
  SUCCESS: globalColors.success.main,
};

const getWindowDimensions = (windowDimensions: any = {}) => {
  const { innerWidth: width, innerHeight: height } = window;

  const orientationType =
    document.body.scrollHeight < document.body.scrollWidth
      ? "landscape-primary"
      : "portrait-primary";

  if (
    orientationType === "landscape-primary" &&
    windowDimensions.orientation === "portrait-primary"
  ) {
    return {
      width: windowDimensions.height,
      height: windowDimensions.width,
      orientation: orientationType,
    };
  }
  return {
    width,
    height,
    orientation: orientationType,
  };
};
interface CameraScanProps {
  onImageCapture?: (
    data: { img: HTMLImageElement; index: number }
  ) => void | Promise<void>;
  onClose?: () => void;
  status: RecognitionStatus;
  message: string;
  captureEnabled?: boolean;
  showNotSureOverlay?: boolean;
  notSureMessage?: string;
  postFramesToNative?: boolean;
  /** Force the in-hole loading spinner (e.g. while models are being recovered). */
  showLoadingOverlay?: boolean;
  /** Optional message shown with the loading spinner (e.g. connection hint). */
  loadingMessage?: string;
}

const CameraScan = ({
  onImageCapture,
  onClose,
  status,
  message,
  captureEnabled = true,
  showNotSureOverlay = false,
  notSureMessage,
  postFramesToNative = false,
  showLoadingOverlay = false,
  loadingMessage,
}: CameraScanProps) => {
  const [cameraLoaded, setCameraLoaded] = useState(false);
  const [cameraKey, setCameraKey] = useState(0);
  const { classes, cx } = useStyles();
  const windowDimensionsRef = useRef<any>(null);
  const [windowDimensions, setWindowDimensions] = useState(
    getWindowDimensions()
  );
  const { t } = useTranslation();
  const webcamRef = useRef<Webcam>(null);
  const userMediaReadyRef = useRef<boolean>(false);
  const loadedDataReadyRef = useRef<boolean>(false);
  const captureIntervalRef = useRef<number | null>(null);
  const cameraReadyTimeoutRef = useRef<number | null>(null);
  const lastResumeAtRef = useRef<number>(0);
  const errorCountRef = useRef<number>(0);
  const currentCaptureRateRef = useRef<number>(250);
  const isFrameInProcessRef = useRef<boolean>(false);

  const isLoading = !cameraLoaded || showLoadingOverlay;

  const clearCaptureInterval = useCallback(() => {
    if (captureIntervalRef.current !== null) {
      clearTimeout(captureIntervalRef.current);
      captureIntervalRef.current = null;
    }
  }, []);

  const clearCameraReadyTimeout = useCallback(() => {
    if (cameraReadyTimeoutRef.current !== null) {
      window.clearTimeout(cameraReadyTimeoutRef.current);
      cameraReadyTimeoutRef.current = null;
    }
  }, []);

  const stopCameraStream = useCallback(() => {
    webcamRef.current?.stream?.getTracks().forEach((track) => {
      track.stop();
    });
  }, []);

  const playCameraVideo = useCallback(() => {
    void webcamRef.current?.video?.play()?.catch(() => {
      // iOS may reject play() until user gesture; getUserMedia usually auto-plays.
    });
  }, []);

  useEffect(() => {
    windowDimensionsRef.current = windowDimensions;
  }, [windowDimensions]);

  /**
   * Adjust capture rate based on error count and device capabilities
   */
  const adjustCaptureRate = useCallback((hasError: boolean) => {
    if (hasError) {
      errorCountRef.current += 1;
      // Increase interval (slow down) when errors occur
      if (errorCountRef.current >= 3) {
        currentCaptureRateRef.current = Math.min(currentCaptureRateRef.current * 1.5, 500); // Max 500ms
        errorCountRef.current = 0; // Reset after adjustment
      }
    } else {
      // Gradually return to normal rate on success
      if (errorCountRef.current > 0) {
        errorCountRef.current = Math.max(0, errorCountRef.current - 1);
      }
      if (currentCaptureRateRef.current > 100) {
        currentCaptureRateRef.current = Math.max(250, currentCaptureRateRef.current * 0.9);
      }
    }

    const adjustedRate = Math.max(250, currentCaptureRateRef.current);
    
    return adjustedRate;
  }, []);

  const capture = useCallback(async () => {
    if (!captureEnabled || !cameraLoaded) {
      return;
    }
    if (!webcamRef.current?.getScreenshot) {
      return;
    }
    if (isFrameInProcessRef.current) {
      return;
    }

    const { width = 0, height = 0 } =
      webcamRef.current?.getCanvas()?.getContext("2d")?.canvas || {};
    if (width === 0 || height === 0) return;
    // console.log("Image canvas: ", {width, height})
    // const isLandscape = width > height;

    const image = new Image();
    let imageSrc: string;
    
    try {
      const screenshot = webcamRef.current.getScreenshot();
      if (!screenshot) {
        return;
      }
      if (windowDimensionsRef.current.orientation === "landscape-primary") {
        imageSrc = await rotateBase64Image(screenshot, 90, CAPTURE_JPEG_QUALITY);
      } else {
        imageSrc = screenshot;
      }
      isFrameInProcessRef.current = true;
      image.src = imageSrc;
      const index = Date.now();
      if (postFramesToNative && status === "SUCCESS") {
        const dataForNativeApp = {
          image: imageSrc,
          index,
        };
        window.postMessage(dataForNativeApp);
      }
      
      image.onload = async () => {
        const data: { img: HTMLImageElement; index: number } = {
          img: image,
          index,
        };
        try {
          await onImageCapture?.(data);
        } finally {
          image.src = "";
          image.onload = null;
          image.onerror = null;
          isFrameInProcessRef.current = false;
        }
      };
      
      image.onerror = () => {
        image.src = "";
        image.onload = null;
        image.onerror = null;
        isFrameInProcessRef.current = false;
      };
    } catch (error) {
      image.src = "";
      image.onload = null;
      image.onerror = null;
      isFrameInProcessRef.current = false;
      console.error("Error capturing image:", error);
    }
  }, [cameraLoaded, captureEnabled, onImageCapture, postFramesToNative, status]);

  useEffect(() => {
    if (!captureEnabled || !cameraLoaded) {
      clearCaptureInterval();
      return;
    }

    // Use adaptive capture rate
    currentCaptureRateRef.current = 300;

    const startCapture = () => {
      if (!captureEnabled || !cameraLoaded) {
        return;
      }
      capture();
      const rate = adjustCaptureRate(status === "ERROR");
      captureIntervalRef.current = window.setTimeout(() => {
        startCapture();
      }, rate);
    };

    startCapture();

    return clearCaptureInterval;
  }, [
    adjustCaptureRate,
    cameraLoaded,
    capture,
    captureEnabled,
    clearCaptureInterval,
    status,
  ]);

  const resumeCameraAfterBackground = useCallback(() => {
    const now = Date.now();
    if (now - lastResumeAtRef.current < 300) {
      return;
    }
    lastResumeAtRef.current = now;

    clearCameraReadyTimeout();
    clearCaptureInterval();
    setCameraLoaded(false);
    userMediaReadyRef.current = false;
    loadedDataReadyRef.current = false;
    stopCameraStream();
    isFrameInProcessRef.current = false;
    errorCountRef.current = 0;
    currentCaptureRateRef.current = 250;
    setCameraKey((prev) => prev + 1);
  }, [clearCameraReadyTimeout, clearCaptureInterval, stopCameraStream]);

  const pauseCameraForBackground = useCallback(() => {
    clearCameraReadyTimeout();
    clearCaptureInterval();
    stopCameraStream();
    isFrameInProcessRef.current = false;
  }, [clearCameraReadyTimeout, clearCaptureInterval, stopCameraStream]);

  useEffect(() => {
    const handleResize = () => {
      const { width, height, orientation } =
        getWindowDimensions(windowDimensions);

      const res = { width, height, orientation };
      setWindowDimensions(res);
    };

    /**
     * On iOS (and some Android browsers), locking the screen or switching apps
     * ends / freezes the camera MediaStream. When the user returns, the <video>
     * element stays in the DOM but renders nothing — so the overlay "hole" shows
     * black instead of a live feed.
     *
     * Incrementing `cameraKey` forces React to unmount and remount <Webcam>,
     * which triggers a fresh getUserMedia() call. Tracks are stopped explicitly
     * so iOS releases the camera before re-acquire.
     */
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        pauseCameraForBackground();
        return;
      }
      resumeCameraAfterBackground();
    };

    const handlePageShow = () => {
      if (document.visibilityState !== "visible") {
        return;
      }
      resumeCameraAfterBackground();
    };

    window.addEventListener("resize", handleResize);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pageshow", handlePageShow);

    return () => {
      window.removeEventListener("resize", handleResize);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pageshow", handlePageShow);
      clearCameraReadyTimeout();
      onClose?.();
    };
  }, [
    clearCameraReadyTimeout,
    onClose,
    pauseCameraForBackground,
    resumeCameraAfterBackground,
  ]);

  const checkCameraReady = useCallback(() => {
    if (cameraLoaded) {
      return;
    }

    if (!userMediaReadyRef.current || !loadedDataReadyRef.current) {
      return;
    }

    clearCameraReadyTimeout();
    cameraReadyTimeoutRef.current = window.setTimeout(() => {
      cameraReadyTimeoutRef.current = null;
      playCameraVideo();
      setCameraLoaded(true);
    }, 500);
  }, [cameraLoaded, clearCameraReadyTimeout, playCameraVideo]);

  const onUserMedia = useCallback(() => {
    // Called when user media stream is successfully obtained
    userMediaReadyRef.current = true;
    checkCameraReady();
  }, [checkCameraReady]);

  const onCameraLoaded = useCallback(() => {
    // Called when video element has loaded its first frame
    loadedDataReadyRef.current = true;
    playCameraVideo();
    checkCameraReady();
  }, [checkCameraReady, playCameraVideo]);

  const handleUserMediaError = useCallback((error: string | DOMException) => {
    if (showNotSureOverlay) {
      return; // Don't close on media error when NOT_SURE overlay is active
    }
    if (onClose) {
      onClose();
    }
  }, [onClose, showNotSureOverlay]);

  const videoConstraints = useMemo(
    () => getCameraVideoConstraints(),
    [windowDimensions]
  );

  return (
    <>
      <div className={classes.webcamHeader}>
        <div className={classes.webcamProgress}>
          <LinearProgress
            color="primary"
            sx={(theme) => ({
              backgroundColor: theme.palette.transparent.main,
              height: "1px",
              "& .MuiLinearProgress-bar": {
                backgroundColor: theme.palette.transparent.main,
                height: "1px",
              },
            })}
          />
        </div>
        <CloseSharpIcon onClick={onClose} />
      </div>
      <Webcam
        key={cameraKey}
        className={classes.webcam}
        audio={false}
        height={windowDimensions.height}
        forceScreenshotSourceSize={true}
        screenshotQuality={CAPTURE_JPEG_QUALITY}
        width={windowDimensions.width}
        ref={webcamRef}
        onUserMedia={onUserMedia}
        onLoadedData={onCameraLoaded}
        allowFullScreen
        screenshotFormat="image/jpeg"
        videoConstraints={videoConstraints}
        onUserMediaError={handleUserMediaError}
      />
      <div className={cx(classes.wrapper, isLoading && classes.wrapperLoading)}>
        <div className={classes.wrapperContent}>
          <div
            className={cx(classes.borderedHole, isLoading && classes.borderedHoleLoading)}
            style={{ borderColor: statusColor[status] }}
          >
            {isLoading && (
              <div className={classes.holeLoadingOverlay}>
                <CircularProgress size={48} sx={{ color: "white" }} />
              </div>
            )}
          </div>
          {message && status === "ERROR" && cameraLoaded && !showLoadingOverlay ? (
            <div className={classes.tooltipWrapper}>
              <div className={classes.messageTooltip}>{t(message)}</div>
            </div>
          ) : null}
          {showLoadingOverlay && loadingMessage ? (
            <div className={classes.tooltipWrapper}>
              <div className={classes.messageTooltip}>{t(loadingMessage)}</div>
            </div>
          ) : null}
          {showNotSureOverlay && notSureMessage ? (
              <div className={classes.tooltipWrapper}>
                <div className={classes.messageTooltip}>{t(notSureMessage)}</div>
              </div>
            ) : null}
        </div>
      </div>
    </>
  );
};

export default CameraScan;
